import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
	BehaviorSubject,
	Observable,
	catchError,
	distinctUntilChanged,
	filter,
	from,
	map,
	of,
	switchMap,
	tap
} from 'rxjs';
import { LanguageNamesService } from 'src/app/core/languages/language-names.service';
import { AnalyticsInsightsEvents } from 'src/app/core/logger/analytics/analytics-insights.events';
import { AnalyticsInsightsListeners } from 'src/app/core/logger/analytics/analytics-insights.listeners';
import { TokenRefreshService } from 'src/app/core/services/token-refresh/token-refresh.service';
import { SettingsService } from 'src/app/core/settings/settings.service';
import { TranslatorService } from 'src/app/core/translator/translator.service';
import { AccessNumbersGlobalService } from '../access-numbers-global/access-numbers-global.service';
import { CustomerRestrictionService } from '../customer-restriction/customer-restriction.service';
import { DropdownService } from '../dropdown/dropdown.service';
import { FeatureFlagsService } from '../feature-flag/feature-flag.service';
import { HabitsHelperService } from '../hello-tomo/habits-helper/habits-helper.service';
import { LifeskillsHelperService } from '../hello-tomo/lifeskills-helper/lifeskills-helper.service';
import { QuestionnaireHelperService } from '../hello-tomo/questionnaire-helper/questionnaire-helper.service';
import { LoggedInUserService } from '../logged-in-user/logged-in-user.service';
import { ProvidedServicesGlobalService } from '../provided-services-global/provided-services-global.service';
import { StackHelperService } from '../stack-helper/stack-helper.service';
import { UserProfileHelperService } from '../user-profile-helper/user-profile-helper.service';
import { RegionService } from '../region/region.service';

@Injectable({
	providedIn: 'root'
})
export class ServicesStartupService {
	settings = inject(SettingsService);
	oidcSecurityService = inject(OidcSecurityService);
	translate = inject(TranslatorService);
	private accessNumbersGlobalService = inject(AccessNumbersGlobalService);
	private providedServicesGlobalService = inject(
		ProvidedServicesGlobalService
	);
	private customerRestrictionService = inject(CustomerRestrictionService);
	private userProfileHelperService = inject(UserProfileHelperService);
	private analyticsInsightsListeners = inject(AnalyticsInsightsListeners);
	private analyticsInsightsEvents = inject(AnalyticsInsightsEvents);
	private dropdownService = inject(DropdownService);
	private stackHelperService = inject(StackHelperService);
	private featureFlagsService = inject(FeatureFlagsService);
	private habitsHelperService = inject(HabitsHelperService);
	private lifeskillsHelperService = inject(LifeskillsHelperService);
	private questionnaireHelperService = inject(QuestionnaireHelperService);
	private loggedInUserService = inject(LoggedInUserService);
	private languageNamesService = inject(LanguageNamesService);
	private tokenRefreshService = inject(TokenRefreshService);
	private regionService = inject(RegionService);

	private mainServicesLoaded: BehaviorSubject<boolean> =
		new BehaviorSubject<boolean>(false);

	/**
	 * Observable for when main services are loaded
	 */
	public mainServicesLoaded$: Observable<boolean> =
		this.mainServicesLoaded.asObservable();

	/**
	 * Registers needed Subscribers and kicks of the initialization of the apps services
	 */
	public initialize(): void {
		this.registerAuthenticatedServices();
	}

	private registerAuthenticatedServices(): void {
		this.oidcSecurityService.isAuthenticated$
			.pipe(
				switchMap(({ isAuthenticated }) => {
					const state = this.oidcSecurityService.getState();
					const authentication$ =
						!isAuthenticated && !state
							? this.oidcSecurityService
									.checkAuth()
									.pipe(
										map((result) => result.isAuthenticated)
									)
							: of(isAuthenticated);

					return authentication$.pipe(
						switchMap((authenticated) =>
							authenticated
								? this.refreshSessionForLatestRoles(
										authenticated
									)
								: of(false)
						)
					);
				}),
				filter(
					(isAuthenticated) =>
						isAuthenticated && !this.mainServicesLoaded.value
				),
				distinctUntilChanged(),
				switchMap(() =>
					from(this.authenticatedServicesLoad()).pipe(
						tap(() => {
							this.mainServicesLoaded.next(true);
							this.tokenRefreshService.startMonitoring();
						})
					)
				)
			)
			.subscribe();
	}

	private refreshSessionForLatestRoles(
		isAuthenticated: boolean
	): Observable<boolean> {
		return this.oidcSecurityService.forceRefreshSession().pipe(
			map((result) => result.isAuthenticated),
			catchError(() => of(isAuthenticated))
		);
	}

	private async authenticatedServicesLoad(): Promise<void> {
		this.regionService.initialize();
		await this.languageNamesService.initialize();
		this.loggedInUserService.initialize();
		this.stackHelperService.initialize();
		this.habitsHelperService.initialize();
		this.lifeskillsHelperService.initialize();
		this.questionnaireHelperService.initialize();
		this.customerRestrictionService.init();
		this.userProfileHelperService.initialize();
		this.analyticsInsightsListeners.initialize();
		this.analyticsInsightsEvents.initialize();
		await this.featureFlagsService.initializeAsync();
		await this.accessNumbersGlobalService.init();
		await this.providedServicesGlobalService.init();
		await this.dropdownService.initialize();

		const tag = document.createElement('script');
		tag.src = 'https://www.youtube.com/iframe_api';
		document.body.appendChild(tag);
	}
}
